import { API_BASE_URL } from './baseUrl'

// Typed client for the backend's /api/player endpoints (see
// backend/docs/auth/api.md). The error codes are a public contract with the
// backend: it adds codes but never renames them.

export type PlayerApiErrorCode =
  | 'PLAYER_INVALID_NAME'
  | 'PLAYER_NAME_NOT_ALLOWED'
  | 'PLAYER_INVALID_DEVICE'
  | 'PLAYER_DEVICE_CLAIMED'
  | 'PLAYER_DEVICE_LIMIT'
  | 'PLAYER_NOT_FOUND'
  | 'AUTH_INVALID_TOKEN'
  | 'AUTH_RATE_LIMITED'
  // Client-side only: the request never got a usable answer.
  | 'PLAYER_NETWORK'
  | 'PLAYER_UNKNOWN'

const KNOWN_SERVER_CODES: ReadonlySet<string> = new Set<PlayerApiErrorCode>([
  'PLAYER_INVALID_NAME',
  'PLAYER_NAME_NOT_ALLOWED',
  'PLAYER_INVALID_DEVICE',
  'PLAYER_DEVICE_CLAIMED',
  'PLAYER_DEVICE_LIMIT',
  'PLAYER_NOT_FOUND',
  'AUTH_INVALID_TOKEN',
  'AUTH_RATE_LIMITED',
])

const REQUEST_TIMEOUT_MS = 15_000

export class PlayerApiError extends Error {
  readonly code: PlayerApiErrorCode
  readonly status?: number

  constructor(code: PlayerApiErrorCode, status?: number) {
    super(code)
    this.name = 'PlayerApiError'
    this.code = code
    this.status = status
  }
}

/** A signed-in player's public identity. `label` is exactly how the leaderboard shows them. */
export interface PlayerProfile {
  displayName: string
  tag: string
  label: string
}

const request = async <T>(
  path: string,
  init: { method: 'GET' | 'POST' | 'PUT'; idToken: string; body?: unknown; deviceId?: string }
): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}/api/player${path}`, {
      method: init.method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${init.idToken}`,
        ...(init.deviceId && { 'X-Device-Id': init.deviceId }),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    throw new PlayerApiError('PLAYER_NETWORK')
  }

  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const serverCode = (payload as { code?: string } | null)?.code
    throw new PlayerApiError(
      serverCode && KNOWN_SERVER_CODES.has(serverCode) ? (serverCode as PlayerApiErrorCode) : 'PLAYER_UNKNOWN',
      response.status
    )
  }

  if (payload === null) throw new PlayerApiError('PLAYER_UNKNOWN', response.status)
  return payload as T
}

const readProfile = (payload: { profile?: PlayerProfile }): PlayerProfile => {
  const profile = payload.profile
  if (!profile || typeof profile.label !== 'string') throw new PlayerApiError('PLAYER_UNKNOWN')
  return profile
}

/**
 * Links this browser to the signed-in account (creating the profile the first
 * time). Safe to repeat. `suggestedName` is the guest name already used on this
 * browser; the backend only uses it when it creates the profile.
 */
export const claimDevice = async (params: {
  idToken: string
  deviceId: string
  suggestedName?: string | null
}): Promise<PlayerProfile> =>
  readProfile(
    await request('/claim-device', {
      method: 'POST',
      idToken: params.idToken,
      deviceId: params.deviceId,
      body: params.suggestedName ? { suggestedName: params.suggestedName } : {},
    })
  )

export const getMyProfile = async (idToken: string): Promise<PlayerProfile> =>
  readProfile(await request('/me', { method: 'GET', idToken }))

export const updateDisplayName = async (idToken: string, displayName: string): Promise<PlayerProfile> =>
  readProfile(await request('/me', { method: 'PUT', idToken, body: { displayName } }))

const MESSAGES: Record<PlayerApiErrorCode, string> = {
  PLAYER_INVALID_NAME: 'Use 2 to 20 letters, numbers or spaces.',
  PLAYER_NAME_NOT_ALLOWED: 'That name is not allowed. Try another.',
  PLAYER_INVALID_DEVICE: 'Something went wrong. Please try again.',
  PLAYER_DEVICE_CLAIMED: 'This browser is already linked to another Zwizle account.',
  PLAYER_DEVICE_LIMIT: 'This account is already linked to too many devices.',
  PLAYER_NOT_FOUND: 'Something went wrong. Please try again.',
  AUTH_INVALID_TOKEN: 'Your session has expired. Sign in again.',
  AUTH_RATE_LIMITED: 'Too many changes. Try again in a while.',
  PLAYER_NETWORK: 'Network error - check your connection.',
  PLAYER_UNKNOWN: 'Something went wrong. Please try again.',
}

export const getPlayerErrorMessage = (error: unknown): string =>
  MESSAGES[error instanceof PlayerApiError ? error.code : 'PLAYER_UNKNOWN']
